import { useEffect } from "react";
import { motion } from "framer-motion";
import DemoForm from "./DemoForm";
import {
  CalendarDays,
  Check,
  Handshake,
  Mail,
  MessageCircle,
  Phone,
  MapPin,
  Send,
} from "lucide-react";
import { primaryCtaClass } from "../utils/ctaClasses";
import { slowFadeUp, slowStagger } from "../utils/animations";
import {
  isDemoFormHash,
  scheduleDemoFormScroll,
} from "../utils/scrollDemoFormIntoView";

const DEMO_SIDEBAR_BG = `${process.env.PUBLIC_URL}/images/request-demo-sidebar-bg.png`;

export default function CTA() {
  useEffect(() => {
    const correctDemoFormHashScroll = () => {
      if (!isDemoFormHash()) return;
      scheduleDemoFormScroll({ updateHash: false });
    };

    correctDemoFormHashScroll();
    window.addEventListener("hashchange", correctDemoFormHashScroll);
    return () =>
      window.removeEventListener("hashchange", correctDemoFormHashScroll);
  }, []);

  const sidebarBullets = [
    "Personalized walkthrough of Gestalt HIS",
    "Expert answers to your workflow questions",
    "No obligation—tailored to your organization",
  ];

  const helpCards = [
    {
      title: "Partner With Us",
      description:
        "Explore strategic alliances, integrations, and long-term collaboration.",
      linkLabel: "Explore Partnership",
      href: "#demo-form",
      Icon: Handshake,
      iconWrap: "bg-blue-100 text-blue-600",
      cardRing: "hover:border-blue-200/80",
    },
    {
      title: "Have a Question?",
      description:
        "Reach out for product details, pricing, or anything else on your mind.",
      linkLabel: "Contact us",
      href: "#demo-form",
      Icon: MessageCircle,
      iconWrap: "bg-amber-100 text-amber-600",
      cardRing: "hover:border-amber-200/80",
    },
  ];

  return (
    <section
      id="pricing"
      className="section-viewport scroll-mt-24 bg-white px-4 py-12 md:px-6 md:py-20"
    >
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto mb-10 max-w-3xl text-center md:mb-14">
          <h2 className="font-display text-section font-bold leading-tight text-secondary">
            Let us Build the Right Healthcare System Together
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted md:text-lg">
            Tell us about your organization and goals—we will tailor a demo to
            your workflows, compliance needs, and growth plans.
          </p>
        </div>

        <motion.div
          variants={slowStagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.08, margin: "0px 0px 120px 0px" }}
          className="mb-10 md:mb-12"
        >
          <h3 className="mb-4 text-center font-display text-lg font-bold text-slate-900 md:text-xl">
            How can we help you?
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 md:max-w-3xl md:mx-auto">
            {helpCards.map((card) => {
              const CardIcon = card.Icon;
              return (
                <motion.article
                  variants={slowFadeUp}
                  key={card.title}
                  className={`flex flex-row items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md md:flex-col md:items-stretch md:gap-0 ${card.cardRing}`}
                >
                  <div
                    className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${card.iconWrap}`}
                  >
                    <CardIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-display text-base font-bold text-slate-900 md:mt-4">
                      {card.title}
                    </h4>
                    <p className="mt-1.5 text-sm font-medium leading-relaxed text-slate-700 md:mt-2">
                      {card.description}
                    </p>
                    <a
                      href={card.href}
                      className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-accent-ink underline-offset-4 transition hover:text-secondary hover:underline md:mt-4"
                    >
                      {card.linkLabel}
                      <span aria-hidden="true">→</span>
                    </a>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </motion.div>

        <div
          id="demo-form"
          className="scroll-mt-32 overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-xl shadow-primary/30 md:scroll-mt-[clamp(8rem,calc(50vh-15rem),18rem)]"
        >
          <div className="grid lg:grid-cols-[minmax(508px,625px)_1fr]">
            <aside className="relative flex min-h-full flex-col overflow-hidden p-6 text-white md:p-8">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${DEMO_SIDEBAR_BG})` }}
                aria-hidden
              />
              <div
                className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/90 to-blue-950/88"
                aria-hidden
              />
              <div className="relative z-10 flex flex-1 flex-col">
                <div
                  className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/10 shadow-inner"
                  aria-hidden
                >
                  <CalendarDays className="h-7 w-7" strokeWidth={1.75} />
                </div>
                <h3 className="font-display mt-6 text-xl font-bold tracking-tight md:text-2xl">
                  Request a Demo
                </h3>
                <p className="mt-2 text-sm leading-[1.7] text-blue-100">
                  See how one platform can unify clinical, operational, and
                  financial workflows.
                </p>
                <ul className="mt-8 space-y-4">
                  {sidebarBullets.map((line) => (
                    <li key={line} className="flex gap-3 text-sm leading-snug text-blue-100">
                      <Check
                        className="mt-0.5 h-5 w-5 shrink-0 text-white"
                        strokeWidth={2.5}
                        aria-hidden
                      />
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            <div className="border-t border-slate-100 bg-white p-6 md:p-8 lg:border-l lg:border-t-0 lg:p-10">
              <DemoForm />
            </div>
          </div>
        </div>

        <motion.div
          id="office"
          variants={slowFadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="scroll-mt-24 mt-10 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm md:mt-12"
        >
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_minmax(0,1fr)]">
            <div className="p-6 md:p-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent-hover">
                Registered office and development center
              </p>
              <h3 className="font-display mt-3 text-xl font-bold text-slate-900 md:text-2xl">
                Gestalt Technologies Pvt Ltd
              </h3>

              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-lg bg-blue-50 p-2 text-blue-600">
                    <Phone className="h-4 w-4" aria-hidden />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Phone Number
                    </p>
                    <a
                      href="tel:+918041724011"
                      className="text-sm font-semibold text-slate-700 transition hover:text-accent-hover"
                    >
                      +91-080-41724011
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-lg bg-blue-50 p-2 text-blue-600">
                    <Mail className="h-4 w-4" aria-hidden />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Email
                    </p>
                    <a
                      href="mailto:support@gestalttechindia.in"
                      className="text-sm font-semibold text-slate-700 transition hover:text-accent-hover"
                    >
                      support@gestalttechindia.in
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <MapPin className="h-4 w-4 text-accent" aria-hidden />
                  Our Office Address
                </div>
                <p className="text-sm leading-relaxed text-slate-700">
                  #15&amp;16 (Old No. 337&amp;336, Khata No.161/2, 1st Main Road, Pai
                  Layout, Udaya Nagar, Benniganahalli, KR Puram, Bengaluru -
                  560016, Karnataka, India.
                </p>
              </div>
            </div>

            <div className="min-h-[300px] border-t border-slate-200 lg:border-l lg:border-t-0">
              <iframe
                title="Gestalt Technologies office map"
                className="h-full min-h-[300px] w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=%2315%2616%20(Old%20No.%20337%26336)%2C%20Khata%20No.161%2F2%2C%201st%20Main%20Road%2C%20Pai%20Layout%2C%20Udaya%20Nagar%2C%20Benniganahalli%2C%20KR%20Puram%2C%20Bengaluru%20560016%2C%20Karnataka%2C%20India&output=embed"
              />
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={slowFadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="relative isolate mt-10 flex flex-col gap-6 overflow-hidden rounded-2xl bg-slate-900 px-6 py-8 text-white shadow-xl md:mt-12 md:flex-row md:items-center md:gap-8 md:px-10 md:py-9"
        >
          <div
            className="pointer-events-none absolute inset-0 -z-20 bg-cover bg-center opacity-22"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/images/compliance-standards-bg.jpg)`,
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-r from-slate-950/95 via-slate-900/90 to-blue-950/88"
            aria-hidden
          />
          <div
            className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-accent text-white"
            aria-hidden
          >
            <Send className="h-6 w-6" strokeWidth={1.75} />
          </div>
          <div className="relative z-10 min-w-0 flex-1">
            <h3 className="font-display text-xl font-bold tracking-tight md:text-2xl">
              Let&apos;s Start the Conversation
            </h3>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-300 md:text-base">
              Whether you&apos;re evaluating HIS options or scaling across sites,
              we&apos;re here to help you move faster—with clarity and confidence.
            </p>
          </div>
          <div className="relative z-10 flex w-full flex-col gap-3 sm:flex-row md:w-auto md:shrink-0">
            <a
              href="#demo-form"
              className={`${primaryCtaClass} gap-2 px-7 py-3.5 text-center text-base font-bold`}
            >
              Request Demo
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
